import { useEffect, useMemo, useState, useCallback } from "react";
import api, { API, extractError } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { KPICard } from "@/components/KPICard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  BadgeIndianRupee, Target, TrendingUp, UserPlus, Repeat2, Sparkles,
  Clock, ClipboardCheck, ClipboardX, Wallet,
  FileSpreadsheet, FileJson, FileText,
} from "lucide-react";
import { formatCompactINR, formatINR, formatNumber, formatPercent } from "@/lib/format";
import { periodPresets, presetToPeriod, formatRange } from "@/lib/periods";
import { toast } from "sonner";

function achievementTone(pct) {
  if (pct >= 100) return "success";
  if (pct >= 60) return "default";
  return "warning";
}

export default function ReportsPage() {
  const initial = useMemo(() => presetToPeriod(periodPresets().this_week), []);
  const [period, setPeriod] = useState(initial);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/reports/period", {
          params: {
            start_date: period.start_date,
            end_date: period.end_date,
            granularity: period.granularity,
          },
        }),
        api.get("/reports/summary", {
          params: { start_date: period.start_date, end_date: period.end_date },
        }),
      ]);
      setData(p);
      setSummary(s);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [period.start_date, period.end_date, period.granularity]);

  useEffect(() => { load(); }, [load]);

  const exportHref = (fmt) => {
    const params = new URLSearchParams({
      start_date: period.start_date,
      end_date: period.end_date,
    });
    return `${API}/reports/export.${fmt}?${params.toString()}`;
  };

  return (
    <div data-testid="reports-page">
      <SectionHeader
        title="Reports"
        description={`Automatic weekly & monthly reporting · ${formatRange(period.start_date, period.end_date)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <PeriodPicker value={period} onChange={setPeriod} testid="reports-period" />
            <Button asChild data-testid="export-xlsx">
              <a href={exportHref("xlsx")} target="_blank" rel="noreferrer">
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
              </a>
            </Button>
            <Button asChild variant="secondary" data-testid="export-csv">
              <a href={exportHref("csv")} target="_blank" rel="noreferrer">
                <FileText className="mr-2 h-4 w-4" /> CSV
              </a>
            </Button>
            <Button asChild variant="secondary" data-testid="export-json">
              <a href={exportHref("json")} target="_blank" rel="noreferrer">
                <FileJson className="mr-2 h-4 w-4" /> JSON
              </a>
            </Button>
          </div>
        }
      />

      {loading || !data ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Loading report…</div>
      ) : (
        <>
          {/* Deposit & Account Achievement */}
          <div className="mb-6">
            <div className="label-caps mb-3">Deposit Performance</div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPICard testid="rep-deposit-target" label="Deposit Target" value={formatCompactINR(data.deposit_target)} icon={Target} hint={`${period.label}`} />
              <KPICard testid="rep-deposit-achieved" label="Deposit Achieved" value={formatCompactINR(data.deposit_achieved)} icon={BadgeIndianRupee} tone="primary" />
              <KPICard
                testid="rep-deposit-pct"
                label="Deposit Achievement %"
                value={formatPercent(data.deposit_achievement_pct)}
                progress={data.deposit_achievement_pct}
                tone={achievementTone(data.deposit_achievement_pct)}
                icon={TrendingUp}
              />
              <KPICard testid="rep-active-clients" label="Active Clients" value={formatNumber(data.active_clients)} icon={Sparkles} tone="success" />
            </div>
          </div>

          <div className="mb-6">
            <div className="label-caps mb-3">Account Opening Performance</div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPICard testid="rep-account-target" label="Account Target" value={formatNumber(Math.round(data.account_target))} icon={Target} hint={`${period.label}`} />
              <KPICard testid="rep-accounts-opened" label="Accounts Opened" value={formatNumber(data.accounts_opened)} icon={UserPlus} tone="primary" />
              <KPICard
                testid="rep-account-pct"
                label="Account Achievement %"
                value={formatPercent(data.account_achievement_pct)}
                progress={data.account_achievement_pct}
                tone={achievementTone(data.account_achievement_pct)}
                icon={TrendingUp}
              />
              <KPICard testid="rep-new-clients" label="New Clients" value={formatNumber(data.new_clients)} icon={UserPlus} hint={formatCompactINR(data.new_client_deposit)} />
            </div>
          </div>

          <div className="mb-6">
            <div className="label-caps mb-3">Client Split & Approvals</div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPICard testid="rep-existing-clients" label="Existing Clients" value={formatNumber(data.existing_clients)} icon={Repeat2} hint={formatCompactINR(data.existing_client_deposit)} />
              <KPICard testid="rep-pending" label="Pending Approvals" value={formatNumber(data.pending_count)} icon={Clock} tone="warning" />
              <KPICard testid="rep-approved" label="Approved Deposits" value={formatCompactINR(data.approved_deposit)} icon={ClipboardCheck} tone="success" hint={`${formatNumber(data.approved_count)} txns`} />
              <KPICard testid="rep-rejected" label="Rejected Deposits" value={formatCompactINR(data.rejected_deposit)} icon={ClipboardX} tone="danger" hint={`${formatNumber(data.rejected_count)} txns`} />
            </div>
          </div>

          {summary && (
            <Card className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="label-caps mb-3">Employee-wise Performance</div>
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="label-caps">Executive</TableHead>
                      <TableHead className="label-caps text-right">Deposit</TableHead>
                      <TableHead className="label-caps text-right">Accounts</TableHead>
                      <TableHead className="label-caps text-right">Active Clients</TableHead>
                      <TableHead className="label-caps text-right">Submissions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.employees.map((e) => (
                      <TableRow key={e.user_id} data-testid={`report-emp-${e.user_id}`} className="hover:bg-muted/40">
                        <TableCell className="text-sm font-medium">{e.user_name || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatINR(e.deposit)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(e.accounts)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(e.active_clients)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(e.submissions)}</TableCell>
                      </TableRow>
                    ))}
                    {summary.employees.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No data for this range.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
